import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    Beaker,
    Users,
    Award,
    TrendingUp,
    BookOpen,
    Zap,
    CheckCircle
} from 'lucide-react';
import Navbar from './Navbar';
import googlePlayBadge from '../../assets/images/google-play-badge.png';

/* -------------------- COUNTRY & PRICING -------------------- */

const EURO_COUNTRIES = [
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR',
    'HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK',
    'SI','ES','SE'
];

const PRICING_BY_REGION = {
    EU: {
        currency: 'EUR',
        symbol: '€',
        single: { monthly: 9.99, yearly: 6.99 },
        family: { monthly: 24.99, yearly: 15.99 }
    },
    UK: {
        currency: 'GBP',
        symbol: '£',
        single: { monthly: 10.99, yearly: 7.99 },
        family: { monthly: 25.99, yearly: 16.99 }
    },
    USA: {
        currency: 'USD',
        symbol: '$',
        single: { monthly: 14.99, yearly: 9.99 },
        family: { monthly: 39.99, yearly: 24.99 }
    },
    NG: {
        currency: 'NGN',
        symbol: '₦',
        single: { monthly: 7999.00, yearly: 5999.00 },
        family: { monthly: 18999.00, yearly: 15999.00 }
    },
    MY: {
        currency: 'MYR',
        symbol: 'RM',
        single: { monthly: 23.99, yearly: 15.56 },
        family: { monthly: 62.99, yearly: 54.99 }
    },
    OTHER: {
        currency: 'USD',
        symbol: '$',
        single: { monthly: 6.99, yearly: 4.99 },
        family: { monthly: 16.99, yearly: 13.99 }
    }
};

const getCountryFromIP = async () => {
    const cached = localStorage.getItem('country_code');
    if (cached) return cached;

    try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        const code = data.country_code || 'US';
        localStorage.setItem('country_code', code);
        return code;
    } catch {
        return 'US';
    }
};

const getRegionByCountry = (code) => {
    if (code === 'GB') return 'UK';
    if (code === 'US') return 'USA';
    if (code === 'NG') return 'NG';
    if (code === 'MY') return 'MY';
    if (EURO_COUNTRIES.includes(code)) return 'EU';
    return 'OTHER';
};

/* -------------------- COMPONENT -------------------- */

const LandingPage = () => {
    const { isAuthenticated } = useAuth();

    // ✅ DEFAULT IS YEARLY
    const [billing, setBilling] = useState('yearly');
    const [region, setRegion] = useState('USA');

    useEffect(() => {
        const init = async () => {
            const country = await getCountryFromIP();
            setRegion(getRegionByCountry(country));
        };
        init();
    }, []);

    const pricing = PRICING_BY_REGION[region];

    const features = [
        {
            icon: Users,
            title: 'Interest-Based Learning',
            description: 'Parents select interests like Space, Aviation, Robotics, or Nature'
        },
        {
            icon: BookOpen,
            title: 'Lesson-First Curriculum',
            description: 'Structured, age-appropriate STEM lessons aligned with learning standards'
        },
        {
            icon: Beaker,
            title: 'Hands-On Experiments',
            description: 'Children learn by completing experiments to understand STEM concepts'
        },
        {
            icon: TrendingUp,
            title: 'Personalized Learning Paths',
            description: 'Each child follows a learning journey based on interests and progress'
        },
        {
            icon: Zap,
            title: 'Short Introductory Videos',
            description: 'Each lesson has a concise video explaining the STEM topic before the experiment'
        },
        {
            icon: Award,
            title: 'Motivation & Achievements',
            description: 'Badges and milestones reward effort, curiosity, and completion'
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* HERO */}
            <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-pink-600 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        STEM Learning Built Around Your Child 🚀
                    </h1>
                    <p className="text-xl md:text-2xl mb-8 text-purple-100">
                        Parents select interests. Children learn STEM through lessons and hands-on experiments.
                    </p>
                    <Link
                        to={isAuthenticated ? '/student/dashboard' : '/login'}
                        className="bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 shadow-lg"
                    >
                        {isAuthenticated ? 'Go to Dashboard' : 'Start for Free'}
                    </Link>
                </div>
            </section>

            {/* FEATURES */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">Why Choose STEM4KIDDO?</h2>
                        <p className="text-xl text-gray-600">
                            Experiment-driven STEM learning designed for children aged 5–12
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((f, i) => (
                            <div key={i} className="bg-white p-6 rounded-xl shadow hover:shadow-xl">
                                <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                                    <f.icon className="w-6 h-6 text-purple-600" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">{f.title}</h3>
                                <p className="text-gray-600">{f.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section className="py-24 bg-white">
                <div className="max-w-5xl mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-4">Our Pricing</h2>
                    <p className="text-xl text-gray-600 mb-8">
                        Choose the plan that fits your family. Cancel anytime.
                    </p>

                    {/* Toggle */}
                    <div className="inline-flex bg-gray-100 rounded-full p-1 mb-12">
                        {['monthly', 'yearly'].map((type) => (
                            <button
                                key={type}
                                onClick={() => setBilling(type)}
                                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                                    billing === type
                                        ? 'bg-white text-purple-600 shadow'
                                        : 'text-gray-600'
                                }`}
                            >
                                {type === 'monthly' ? 'Monthly' : 'Yearly'}
                            </button>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* SINGLE */}
                        <div className="border rounded-2xl p-8">
                            <h3 className="text-2xl font-bold mb-2">Individual Plan</h3>
                            <p className="text-gray-600 mb-6">Perfect for one learner</p>

                            <p className="text-4xl font-bold">
                                {pricing.symbol}{pricing.single[billing]}
                                <span className="text-lg text-gray-500"> /month</span>
                            </p>

                            {billing === 'yearly' && (
                                <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                            )}

                            <ul className="space-y-3 my-8 text-left">
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> 1 child profile</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Personalized learning path</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Lesson-based STEM curriculum</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Short explanatory videos</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> 7-day free trial</li>
                            </ul>

                            <Link to="/parent/dashboard" className="block bg-purple-600 text-white py-3 rounded-xl font-bold">
                                Start Free Trial
                            </Link>
                        </div>

                        {/* FAMILY */}
                        <div className="border-2 border-purple-600 bg-purple-50 rounded-2xl p-8 relative">
                            <span className="absolute top-4 right-4 bg-purple-600 text-white text-xs px-3 py-1 rounded-full">
                                Most Popular
                            </span>

                            <h3 className="text-2xl font-bold mb-2">Family Plan</h3>
                            <p className="text-gray-600 mb-6">Best value for families</p>

                            <p className="text-4xl font-bold">
                                {pricing.symbol}{pricing.family[billing]}
                                <span className="text-lg text-gray-500"> /month</span>
                            </p>

                            {billing === 'yearly' && (
                                <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                            )}

                            <ul className="space-y-3 my-8 text-left">
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Up to 3 children</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Personalized learning paths</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Structured STEM lessons</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Short explanatory videos</li>
                                <li className="flex gap-2"><CheckCircle className="text-purple-600" /> Shared parent dashboard</li>
                            </ul>

                            <Link to="/parent/dashboard" className="block bg-purple-600 text-white py-3 rounded-xl font-bold">
                                Get Family Plan
                            </Link>
                        </div>
                    </div>

                    <p className="mt-8 text-sm text-gray-500">
                        Prices shown in {pricing.currency}. Taxes may apply.
                    </p>
                </div>
            </section>
            {/*<section className="py-24 bg-white">*/}
                <center>
                    <a href="https://play.google.com/store/apps/details?id=pt.fancybox.stem4kiddo" target="_blank"><img className="h-36 w-auto" src={googlePlayBadge} alt="Download on Google Play Store"/> </a>
                </center>
            {/*</section>*/}

            {/* FOOTER */}
            <footer className="bg-gray-900 text-white py-10 text-center text-gray-400">
                Powered by Fancybox LDA. All rights reserved. |{' '}
                <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            </footer>
        </div>
    );
};

export default LandingPage;